import { filterPropertyTypes } from 'boot/globals';

/**
 * How a criterion's value is edited in the dialog's detail pane.
 *
 * Derived rather than authored: callers already describe a criterion by its `filterType`, and the
 * dialog should not need a second field kept in sync with the first.
 *
 * @typedef {'chips'|'optionList'|'picker'|'text'|'numeric'|'dateRange'} EditorKind
 */
export const EDITOR_KINDS = {
  /** Small, static, self-explanatory sets. Rendered as a row of filter chips. */
  CHIPS: 'chips',
  /** Larger or asynchronously loaded sets. Searchable list with the selection shown as chips. */
  OPTION_LIST: 'optionList',
  /** Clients and users, which keep their table so the disambiguating columns survive. */
  PICKER: 'picker',
  /** Free text with an operator. */
  TEXT: 'text',
  /** Numbers and durations, both operator plus one or two values. */
  NUMERIC: 'numeric',
  /** Date presets plus a calendar. */
  DATE_RANGE: 'dateRange',
};

/**
 * Above this, a chip row stops being scannable and becomes a wall.
 * Product Design set the number; it is here rather than inline so the editors and their tests
 * cannot drift apart on it.
 */
export const MAX_CHIP_OPTIONS = 8;

/*
 * A country flag is an image, which a chip has no room for beside its label and its check. Country
 * and state are built from the clinic's own clients, so the count alone would give a small clinic
 * chips without flags and a large one the list with them.
 */
const hasFlags = (items) => items.some((item) => item?.flagIcon);

const KIND_BY_FILTER_TYPE = {
  [filterPropertyTypes.Client]: EDITOR_KINDS.PICKER,
  [filterPropertyTypes.User]: EDITOR_KINDS.PICKER,
  [filterPropertyTypes.ArrayContainsUser]: EDITOR_KINDS.PICKER,
  [filterPropertyTypes.StringMatch]: EDITOR_KINDS.TEXT,
  [filterPropertyTypes.Number]: EDITOR_KINDS.NUMERIC,
  [filterPropertyTypes.Duration]: EDITOR_KINDS.NUMERIC,
  [filterPropertyTypes.Date]: EDITOR_KINDS.DATE_RANGE,
  [filterPropertyTypes.Boolean]: EDITOR_KINDS.CHIPS,
};

/**
 * Resolve which editor a criterion uses.
 *
 * The enum types (StringInArray, ArrayInArray, NullValue) split on how many options they carry:
 * a short synchronous set reads well as chips, anything longer needs search. An empty option list
 * means the options are still arriving, so it takes the searchable editor rather than briefly
 * rendering as chips and then reflowing once they land.
 *
 * @param {{ filterType: string, options?: { filterCriteriaItems?: unknown[] } }} criterion
 * @returns {EditorKind}
 */
export function getEditorKind(criterion) {
  const mapped = KIND_BY_FILTER_TYPE[criterion?.filterType];
  if (mapped !== undefined) {
    return mapped;
  }

  const items = criterion?.options?.filterCriteriaItems;
  const isShortSynchronousSet = Array.isArray(items)
    && items.length > 0
    && items.length <= MAX_CHIP_OPTIONS
    && !hasFlags(items);

  return isShortSynchronousSet ? EDITOR_KINDS.CHIPS : EDITOR_KINDS.OPTION_LIST;
}
